import cn from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import ActionIcon from 'components/BaseHelpers/ActionIcon';
import Text from 'components/BaseHelpers/Text';

import { PAGE_ITEM_COUNT } from '../../usePageSearch';

import styles from './SearchPagination.module.scss';

export type PaginationLabels = {
  paginationLabelPrevious: string;
  paginationLabelNext: string;
};

export type SearchPaginationProps = {
  totalResults: number;
  labels?: PaginationLabels;
  adjustPage: (newPage: number) => void;
  defaultPage?: string;
};

const SearchPagination = ({
  adjustPage,
  totalResults,
  labels,
  defaultPage = '1',
}: SearchPaginationProps): JSX.Element => {
  const { paginationLabelPrevious = 'Prev', paginationLabelNext = 'Next' } = labels ?? {};
  const searchParams = useSearchParams();

  const currentPage = parseInt(searchParams?.get('page') ?? defaultPage);
  const pageCount = Math.ceil(totalResults / PAGE_ITEM_COUNT);

  const paginations = useMemo(() => {
    const pages = [...new Array(pageCount)].map((_, index) => {
      const page = (index + 1).toString();
      const updatedQueries = new URLSearchParams(searchParams);
      updatedQueries.set('page', page);
      return {
        page,
        isActive: isNaN(currentPage) ? false : currentPage === index + 1,
        pageNumber: index + 1,
      };
    });
    return pages;
  }, [pageCount, searchParams, currentPage]);

  const canPaginateBack = currentPage > 1;
  const canPaginateForwards = currentPage < paginations.length;

  return (
    <div className={styles.main}>
      <button
        onClick={() => adjustPage(currentPage - 1)}
        className={styles.link}
        disabled={!canPaginateBack}
        aria-label="Go to previous results page."
      >
        <ActionIcon wrapperClass={styles.previousIconWrapper} className={styles.previousIcon} />
        <Text tag="span" field={paginationLabelPrevious} />
      </button>

      <ul className={styles.pageList}>
        {paginations.map(({ page, isActive, pageNumber }) => {
          return (
            <li
              className={cn(styles.pageItem, {
                [styles.active]: isActive,
                [styles.activeSibling]: currentPage === pageNumber + 1,
              })}
              key={page}
            >
              <button
                onClick={() => adjustPage(pageNumber)}
                className={cn(styles.pageLink, {
                  [styles.activePage]: isActive,
                })}
                aria-label={`Page ${page}.`}
                aria-current={isActive ? `page` : 'false'}
              >
                {page}
              </button>
            </li>
          );
        })}
      </ul>
      <button
        onClick={() => adjustPage(currentPage + 1)}
        className={styles.link}
        aria-label="Go to next results page."
        disabled={!canPaginateForwards}
      >
        <Text tag="span" field={paginationLabelNext} />
        <ActionIcon wrapperClass={styles.nextIconWrapper} />
      </button>
    </div>
  );
};

export default SearchPagination;
